import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Play, Pause, StopCircle, BarChart3, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface Experiment {
  id: string;
  name: string;
  description: string;
  page_path: string;
  status: string;
  conversion_goal: string;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
}

interface Variant {
  id: string;
  experiment_id: string;
  name: string;
  description: string;
  traffic_allocation: number;
  is_control: boolean;
  config: Record<string, any>;
}

export function ABTestManager() {
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedExperiment, setSelectedExperiment] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pagePath, setPagePath] = useState('/');
  const [conversionGoal, setConversionGoal] = useState('');
  const [variants, setVariants] = useState<
    Array<{ name: string; description: string; allocation: number; isControl: boolean }>
  >([
    { name: 'Control', description: 'Original version', allocation: 50, isControl: true },
    { name: 'Variant A', description: 'Test version', allocation: 50, isControl: false },
  ]);

  useEffect(() => {
    loadExperiments();
  }, []);

  const loadExperiments = async () => {
    try {
      const { data, error } = await supabase
        .from('ab_experiments')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setExperiments(data || []);
    } catch (error) {
      console.error('Error loading experiments:', error);
      toast.error('Failed to load experiments');
    } finally {
      setLoading(false);
    }
  };

  const createExperiment = async () => {
    try {
      // Validate traffic allocation
      const totalAllocation = variants.reduce((sum, v) => sum + v.allocation, 0);
      if (totalAllocation !== 100) {
        toast.error('Traffic allocation must total 100%');
        return;
      }

      // Create experiment
      const { data: experiment, error: expError } = await supabase
        .from('ab_experiments')
        .insert({
          name,
          description,
          page_path: pagePath,
          conversion_goal: conversionGoal,
          status: 'draft',
        })
        .select()
        .single();

      if (expError) throw expError;

      // Create variants
      const variantInserts = variants.map((v) => ({
        experiment_id: experiment.id,
        name: v.name,
        description: v.description,
        traffic_allocation: v.allocation,
        is_control: v.isControl,
        config: {},
      }));

      const { error: varError } = await supabase.from('ab_variants').insert(variantInserts);

      if (varError) throw varError;

      toast.success('Experiment created successfully');
      setShowCreateDialog(false);
      resetForm();
      loadExperiments();
    } catch (error: any) {
      console.error('Error creating experiment:', error);
      toast.error(error.message || 'Failed to create experiment');
    }
  };

  const updateExperimentStatus = async (experimentId: string, status: string) => {
    try {
      const updateData: any = { status };

      if (status === 'active' && !experiments.find((e) => e.id === experimentId)?.started_at) {
        updateData.started_at = new Date().toISOString();
      }

      if (status === 'completed') {
        updateData.ended_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('ab_experiments')
        .update(updateData)
        .eq('id', experimentId);

      if (error) throw error;

      toast.success(
        `Experiment ${status === 'active' ? 'started' : status === 'paused' ? 'paused' : 'stopped'}`
      );
      loadExperiments();
    } catch (error: any) {
      console.error('Error updating experiment:', error);
      toast.error('Failed to update experiment');
    }
  };

  const deleteExperiment = async (experimentId: string) => {
    if (
      !confirm(
        'Are you sure you want to delete this experiment? This will also delete all variants and data.'
      )
    ) {
      return;
    }

    try {
      const { error } = await supabase.from('ab_experiments').delete().eq('id', experimentId);

      if (error) throw error;

      toast.success('Experiment deleted');
      loadExperiments();
    } catch (error: any) {
      console.error('Error deleting experiment:', error);
      toast.error('Failed to delete experiment');
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setPagePath('/');
    setConversionGoal('');
    setVariants([
      { name: 'Control', description: 'Original version', allocation: 50, isControl: true },
      { name: 'Variant A', description: 'Test version', allocation: 50, isControl: false },
    ]);
  };

  const addVariant = () => {
    setVariants([
      ...variants,
      {
        name: `Variant ${String.fromCharCode(65 + variants.length - 1)}`,
        description: '',
        allocation: 0,
        isControl: false,
      },
    ]);
  };

  const removeVariant = (index: number) => {
    if (variants.length <= 2) {
      toast.error('Must have at least 2 variants');
      return;
    }
    setVariants(variants.filter((_, i) => i !== index));
  };

  const updateVariant = (index: number, field: string, value: any) => {
    const updated = [...variants];
    updated[index] = { ...updated[index], [field]: value };
    setVariants(updated);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      draft: { label: 'Draft', variant: 'outline' },
      active: { label: 'Active', variant: 'default', className: 'bg-green-500' },
      paused: { label: 'Paused', variant: 'secondary' },
      completed: { label: 'Completed', variant: 'secondary' },
    };
    const config = variants[status] || variants.draft;
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">A/B Testing</h2>
          <p className="text-muted-foreground">
            Create and manage A/B tests to optimize conversion
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Experiment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create A/B Test Experiment</DialogTitle>
              <DialogDescription>
                Set up a new experiment to test different variants
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Experiment Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Homepage Hero Test"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Testing different hero section designs..."
                />
              </div>
              <div>
                <Label htmlFor="pagePath">Page Path</Label>
                <Input
                  id="pagePath"
                  value={pagePath}
                  onChange={(e) => setPagePath(e.target.value)}
                  placeholder="/"
                />
              </div>
              <div>
                <Label htmlFor="conversionGoal">Conversion Goal</Label>
                <Input
                  id="conversionGoal"
                  value={conversionGoal}
                  onChange={(e) => setConversionGoal(e.target.value)}
                  placeholder="signup, purchase, click_cta"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Variants</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addVariant}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Variant
                  </Button>
                </div>
                <div className="space-y-3">
                  {variants.map((variant, index) => (
                    <Card key={index}>
                      <CardContent className="pt-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Name</Label>
                            <Input
                              value={variant.name}
                              onChange={(e) => updateVariant(index, 'name', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Traffic % </Label>
                            <div className="flex gap-2">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={variant.allocation}
                                onChange={(e) =>
                                  updateVariant(index, 'allocation', parseInt(e.target.value) || 0)
                                }
                              />
                              {!variant.isControl && variants.length > 2 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeVariant(index)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="col-span-2">
                            <Label>Description</Label>
                            <Input
                              value={variant.description}
                              onChange={(e) => updateVariant(index, 'description', e.target.value)}
                            />
                          </div>
                        </div>
                        {variant.isControl && (
                          <Badge variant="outline" className="mt-2">
                            Control Group
                          </Badge>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Total allocation: {variants.reduce((sum, v) => sum + v.allocation, 0)}% (must be
                  100%)
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={createExperiment}>Create Experiment</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Experiments</CardTitle>
          <CardDescription>Manage your A/B testing experiments</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-8">Loading experiments...</p>
          ) : experiments.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No experiments yet. Create one to get started.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Page</TableHead>
                  <TableHead>Goal</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {experiments.map((exp) => (
                  <TableRow key={exp.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{exp.name}</div>
                        {exp.description && (
                          <div className="text-sm text-muted-foreground">{exp.description}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs">{exp.page_path}</code>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs">{exp.conversion_goal}</code>
                    </TableCell>
                    <TableCell>{getStatusBadge(exp.status)}</TableCell>
                    <TableCell className="text-sm">
                      {new Date(exp.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {exp.status === 'draft' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateExperimentStatus(exp.id, 'active')}
                          >
                            <Play className="h-3 w-3" />
                          </Button>
                        )}
                        {exp.status === 'active' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateExperimentStatus(exp.id, 'paused')}
                            >
                              <Pause className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateExperimentStatus(exp.id, 'completed')}
                            >
                              <StopCircle className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        {exp.status === 'paused' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateExperimentStatus(exp.id, 'active')}
                          >
                            <Play className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            (window.location.href = `/analytics/ab-testing?experiment=${exp.id}`)
                          }
                        >
                          <BarChart3 className="h-3 w-3" />
                        </Button>
                        {exp.status === 'draft' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteExperiment(exp.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
